import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shared/PageHeader';
import { useAdminMetric } from '@/hooks/useAdminMetric';
import { formatCents } from '@/lib/formatCents';
import { AcquisitionCohortTable } from '@/components/admin/metrics/AcquisitionCohortTable';
import {
  adminMetricsService,
  type MetricsAcquisitionCohorts,
} from '@/services/adminMetricsService';

// F133 — acquisition cohorts by source/code. 12 trailing months, mirroring
// the F062 cohorts() default window.
const MONTHS = 12;

function TableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} data-testid="acquisition-skeleton" className="h-8 w-full rounded-md" />
      ))}
    </div>
  );
}

/**
 * Founder-facing per-acquisition-source cohort read (MARKETING.md §4/§7):
 * signups → trials → paying → monthly logo churn per promo code / ref /
 * self-reported source, with month 4 (first full-price month) highlighted
 * and a rough per-code commission-accrued estimate — so the affiliate
 * pilot's kill/scale triggers become a 5-minute monthly read.
 */
export function AdminAcquisitionPage() {
  const { t } = useTranslation();

  const [state, reload] = useAdminMetric<MetricsAcquisitionCohorts>(
    useCallback(() => adminMetricsService.getAcquisitionCohorts(MONTHS), [])
  );

  function renderBody() {
    if (state.status === 'loading') {
      return <TableSkeleton />;
    }

    if (state.status === 'error') {
      return (
        <div
          data-testid="acquisition-error"
          role="alert"
          className="flex items-center gap-3 text-sm text-destructive"
        >
          <span>{state.error}</span>
          <Button variant="ghost" size="sm" onClick={reload} aria-label={t('admin.metrics.retry')}>
            {t('admin.metrics.retry')}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <AcquisitionCohortTable groups={state.data.groups} formatCents={formatCents} />

        <p className="text-xs text-muted-foreground">{t('admin.metrics.acquisition.note')}</p>

        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">
            {t('admin.metrics.acquisition.legend.title')}
          </p>
          <p>{t('admin.metrics.acquisition.legend.kill')}</p>
          <p>{t('admin.metrics.acquisition.legend.scale')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin.metrics.acquisition.title')}
        subtitle={t('admin.metrics.acquisition.subtitle')}
      />
      {renderBody()}
    </div>
  );
}
